import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { ActivityScheduleSimpleService, ScheduledActivity } from '../../services/activity-schedule-simple.service';

interface CalendarView {
  key: string;
  label: string;
  icon: string;
}

@Component({
  selector: 'app-programari-simple',
  templateUrl: './programari-simple.component.html',
  styleUrls: ['./programari.component.scss']
})
export class ProgramariSimpleComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Calendar state
  currentView: string = 'month';
  currentDate = new Date();
  selectedDate = new Date();
  
  // Data
  activities: ScheduledActivity[] = [];
  filteredActivities: ScheduledActivity[] = [];
  isLoading = false;
  
  // View options
  calendarViews: CalendarView[] = [
    { key: 'month', label: 'Lunar', icon: 'calendar_view_month' },
    { key: 'week', label: 'Săptămânal', icon: 'view_week' },
    { key: 'day', label: 'Zilnic', icon: 'view_day' },
    { key: 'agenda', label: 'Agendă', icon: 'list' }
  ];

  // Filters
  selectedStatuses: string[] = ['scheduled', 'rescheduled'];
  selectedActivityTypes: number[] = [];
  searchTerm = '';
  
  // Mock data
  statusOptions = [
    { key: 'scheduled', label: 'Programată', color: '#2196F3' },
    { key: 'completed', label: 'Finalizată', color: '#4CAF50' },
    { key: 'cancelled', label: 'Anulată', color: '#F44336' },
    { key: 'rescheduled', label: 'Reprogramată', color: '#FF9800' },
    { key: 'no_show', label: 'Absent', color: '#9E9E9E' }
  ];

  activityTypes = [
    { id: 1, name: 'Vizită la domiciliu' },
    { id: 2, name: 'Întâlnire birou' },
    { id: 3, name: 'Evaluare copil' },
    { id: 4, name: 'Ședință echipă' },
    { id: 5, name: 'Consultanță' }
  ];

  // Calendar display
  monthNames = [
    'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
    'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
  ];

  weekDays = ['Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'];
  weekDaysShort = ['L', 'Ma', 'Mi', 'J', 'V', 'S', 'D'];

  constructor(
    private activityScheduleService: ActivityScheduleSimpleService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadActivities();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadActivities(): void {
    this.isLoading = true;
    
    // Simulate loading activities
    this.activityScheduleService.getCalendarActivities(1, this.getDateString(this.currentDate), this.getEndDateString()).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (activities) => {
        this.activities = activities;
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading activities:', error);
        this.isLoading = false;
      }
    });
  }


  private getDateString(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private getEndDateString(): string {
    const endDate = new Date(this.currentDate);
    endDate.setMonth(endDate.getMonth() + 1);
    return this.getDateString(endDate);
  }

  // View management
  changeView(viewKey: string): void {
    this.currentView = viewKey;
  }

  getCurrentViewLabel(): string {
    const view = this.calendarViews.find(v => v.key === this.currentView);
    return view?.label || 'Lunar';
  }

  // Navigation
  navigatePrevious(): void {
    switch (this.currentView) {
      case 'month':
        this.currentDate.setMonth(this.currentDate.getMonth() - 1);
        break;
      case 'week':
        this.currentDate.setDate(this.currentDate.getDate() - 7);
        break;
      case 'day':
        this.currentDate.setDate(this.currentDate.getDate() - 1);
        break;
    }
    this.currentDate = new Date(this.currentDate);
    this.loadActivities();
  }

  navigateNext(): void {
    switch (this.currentView) {
      case 'month':
        this.currentDate.setMonth(this.currentDate.getMonth() + 1);
        break;
      case 'week':
        this.currentDate.setDate(this.currentDate.getDate() + 7);
        break;
      case 'day':
        this.currentDate.setDate(this.currentDate.getDate() + 1);
        break;
    }
    this.currentDate = new Date(this.currentDate);
    this.loadActivities();
  }

  navigateToday(): void {
    this.currentDate = new Date();
    this.selectedDate = new Date();
    this.loadActivities();
  }

  // Filtering
  applyFilters(): void {
    let filtered = [...this.activities];

    // Filter by status
    if (this.selectedStatuses.length > 0) {
      filtered = filtered.filter(activity => 
        this.selectedStatuses.includes(activity.status)
      );
    }

    // Filter by activity type
    if (this.selectedActivityTypes.length > 0) {
      filtered = filtered.filter(activity =>
        activity.activity_type && this.selectedActivityTypes.includes(activity.activity_type.id)
      );
    }

    // Filter by search term
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(activity =>
        activity.title.toLowerCase().includes(term) ||
        (activity.notes && activity.notes.toLowerCase().includes(term)) ||
        (activity.activity_type && activity.activity_type.name.toLowerCase().includes(term))
      );
    }

    this.filteredActivities = filtered.sort((a, b) => {
      const dateCompare = a.scheduled_date.localeCompare(b.scheduled_date);
      if (dateCompare === 0) {
        return a.start_time.localeCompare(b.start_time);
      }
      return dateCompare;
    });
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  onStatusFilterChange(): void {
    this.applyFilters();
  }

  onActivityTypeFilterChange(): void {
    this.applyFilters();
  }

  // Calendar display helpers
  getMonthYear(): string {
    const month = this.monthNames[this.currentDate.getMonth()];
    const year = this.currentDate.getFullYear();
    return `${month} ${year}`;
  }

  getWeekRange(): string {
    const startOfWeek = new Date(this.currentDate);
    startOfWeek.setDate(this.currentDate.getDate() - this.currentDate.getDay() + 1);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
      return `${startOfWeek.getDate()} - ${endOfWeek.getDate()} ${this.monthNames[startOfWeek.getMonth()]} ${startOfWeek.getFullYear()}`;
    } else {
      return `${startOfWeek.getDate()} ${this.monthNames[startOfWeek.getMonth()]} - ${endOfWeek.getDate()} ${this.monthNames[endOfWeek.getMonth()]} ${startOfWeek.getFullYear()}`;
    }
  }

  getDayDate(): string {
    return `${this.currentDate.getDate()} ${this.monthNames[this.currentDate.getMonth()]} ${this.currentDate.getFullYear()}`;
  }

  getActivitiesForCurrentView(): ScheduledActivity[] {
    const currentDateStr = this.getDateString(this.currentDate);
    
    switch (this.currentView) {
      case 'day':
        return this.filteredActivities.filter(activity => 
          activity.scheduled_date === currentDateStr
        );
      case 'week':
        const startOfWeek = new Date(this.currentDate);
        startOfWeek.setDate(this.currentDate.getDate() - this.currentDate.getDay() + 1);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        
        return this.filteredActivities.filter(activity => {
          const activityDate = new Date(activity.scheduled_date);
          return activityDate >= startOfWeek && activityDate <= endOfWeek;
        });
      case 'agenda':
        return this.filteredActivities.slice(0, 50); // Show next 50 activities
      default: // month
        const startOfMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
        const endOfMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0);
        
        return this.filteredActivities.filter(activity => {
          const activityDate = new Date(activity.scheduled_date);
          return activityDate >= startOfMonth && activityDate <= endOfMonth;
        });
    }
  }

  // Activity actions
  scheduleNewActivity(): void {
    this.notificationService.warningSwal(
      'Programare Nouă',
      'Funcționalitatea de programare va fi activată după configurarea completă a backend-ului.',
      'info'
    );
  }

  viewActivityDetails(activity: ScheduledActivity): void {
    console.log('View activity details:', activity);
    this.notificationService.warningSwal(
      'Detalii Activitate',
      `${activity.title}\nData: ${activity.scheduled_date}\nOra: ${activity.start_time}\nStatus: ${this.getStatusLabel(activity.status)}`,
      'info'
    );
  }

  editActivity(activity: ScheduledActivity, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    console.log('Edit activity:', activity);
    this.notificationService.warningSwal(
      'Editare Activitate',
      'Funcționalitatea de editare va fi disponibilă după configurarea backend-ului.',
      'info'
    );
  }

  deleteActivity(activity: ScheduledActivity, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    this.notificationService.warningSwal(
      'Confirmare',
      `Sunteți sigur că doriți să ștergeți activitatea "${activity.title}"?`,
      'warning'
    ).then((result) => {
      if (result.isConfirmed) {
        this.activityScheduleService.deleteActivity(activity.id).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: () => {
            this.loadActivities(); // Reload from backend
            this.notificationService.successSwal(
              'Succes',
              'Activitatea a fost ștearsă cu succes!'
            );
          },
          error: (error) => {
            console.error('Error deleting activity:', error);
            this.notificationService.warningSwal(
              'Eroare',
              'Nu s-a putut șterge activitatea. Încercați din nou.',
              'error'
            );
          }
        });
      }
    });
  }

  // Export functions
  exportToPDF(): void {
    this.notificationService.successSwal(
      'Export PDF',
      'Funcționalitatea de export PDF va fi disponibilă după configurarea backend-ului.'
    );
  }

  exportToExcel(): void {
    this.notificationService.successSwal(
      'Export Excel', 
      'Funcționalitatea de export Excel va fi disponibilă după configurarea backend-ului.'
    );
  }

  // Utility functions
  getStatusLabel(status: string): string {
    const option = this.statusOptions.find(opt => opt.key === status);
    return option?.label || status;
  }

  getStatusColor(status: string): string {
    const option = this.statusOptions.find(opt => opt.key === status);
    return option?.color || '#6c757d';
  }

  formatTime(time: string): string {
    return time.substring(0, 5); // HH:MM from HH:MM:SS
  }

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'short'
    });
  }

  formatDateTime(dateStr: string, timeStr: string): string {
    const date = new Date(dateStr);
    return `${date.toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })} la ${this.formatTime(timeStr)}`;
  }

  trackByActivityId(index: number, activity: ScheduledActivity): number {
    return activity.id;
  }
}